import { useState } from 'react'
import './App.css'
import NewsSection from './NewsSection'
import SEO from './components/SEO'
import ShopSection from './components/ShopSection'

function App() {
  const [activeTab, setActiveTab] = useState('home')
  const [searchQuery, setSearchQuery] = useState('')

  // Navigation items
  const navItems: Array<{id: string; label: string}> = []

  // SCI Discipline Categories
  const categories = [
    { id: 'physics', name: 'Physics', icon: '⚛️', count: 52 },
    { id: 'chemistry', name: 'Chemistry', icon: '🧪', count: 48 },
    { id: 'biology', name: 'Biology', icon: '🧬', count: 65 },
    { id: 'medicine', name: 'Medicine', icon: '🏥', count: 78 },
    { id: 'engineering', name: 'Engineering', icon: '⚙️', count: 55 },
    { id: 'computer', name: 'Computer Science', icon: '💻', count: 62 },
    { id: 'environment', name: 'Environmental Science', icon: '🌍', count: 38 },
    { id: 'materials', name: 'Materials Science', icon: '🔬', count: 41 }
  ]

  // Featured SCI KOLs (Key Opinion Leaders)
  const featuredKOLs = [
    {
      id: 1,
      name: 'Prof. Jennifer Doudna',
      institution: 'UC Berkeley',
      researchArea: 'CRISPR-Cas9 Gene Editing, Biochemistry',
      hIndex: 134,
      url: 'https://vcresearch.berkeley.edu/faculty/jennifer-doudna',
      category: 'biology',
      awards: ['Nobel Prize 2020'],
      tags: ['crispr', 'gene editing', 'biochemistry']
    },
    {
      id: 2,
      name: 'Prof. Kip Thorne',
      institution: 'Caltech',
      researchArea: 'Gravitational Physics, Astrophysics',
      hIndex: 121,
      url: 'https://www.kipthorne.caltech.edu/',
      category: 'physics',
      awards: ['Nobel Prize 2017'],
      tags: ['gravitational waves', 'astrophysics', 'relativity']
    },
    {
      id: 3,
      name: 'Prof. Frances Arnold',
      institution: 'Caltech',
      researchArea: 'Directed Evolution, Protein Engineering',
      hIndex: 98,
      url: 'https://www.che.caltech.edu/faculty/arnold_f',
      category: 'chemistry',
      awards: ['Nobel Prize 2018'],
      tags: ['directed evolution', 'enzymes', 'sustainability']
    },
    {
      id: 4,
      name: 'Prof. James P. Allison',
      institution: 'MD Anderson Cancer Center',
      researchArea: 'Cancer Immunotherapy, T-cell Biology',
      hIndex: 156,
      url: 'https://www.mdanderson.org/research/departments-labs-institutes/labs/allison-lab.html',
      category: 'medicine',
      awards: ['Nobel Prize 2018'],
      tags: ['immunotherapy', 'cancer', 'immunology']
    },
    {
      id: 5,
      name: 'Prof. Yoshua Bengio',
      institution: 'University of Montreal',
      researchArea: 'Deep Learning, Artificial Intelligence',
      hIndex: 178,
      url: 'https://mila.quebec/en/yoshua-bengio/',
      category: 'computer',
      awards: ['Turing Award 2018'],
      tags: ['deep learning', 'ai', 'neural networks']
    },
    {
      id: 6,
      name: 'Prof. John B. Goodenough',
      institution: 'University of Texas at Austin',
      researchArea: 'Materials Science, Lithium-ion Batteries',
      hIndex: 145,
      url: 'https://me.utexas.edu/people/faculty-directory/goodenough',
      category: 'materials',
      awards: ['Nobel Prize 2019'],
      tags: ['batteries', 'energy storage', 'materials']
    }
  ]

  // Top SCI Research Institutions
  const topInstitutions = [
    { name: 'Harvard University', location: 'USA', rank: 1, researchers: '2,500+' },
    { name: 'MIT', location: 'USA', rank: 2, researchers: '1,800+' },
    { name: 'Stanford University', location: 'USA', rank: 3, researchers: '2,100+' },
    { name: 'University of Cambridge', location: 'UK', rank: 4, researchers: '1,900+' },
    { name: 'Caltech', location: 'USA', rank: 5, researchers: '950+' },
    { name: 'ETH Zurich', location: 'Switzerland', rank: 6, researchers: '1,200+' }
  ]

  // Filter KOLs based on search
  const filteredKOLs = featuredKOLs.filter(kol =>
    kol.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    kol.institution.toLowerCase().includes(searchQuery.toLowerCase()) ||
    kol.researchArea.toLowerCase().includes(searchQuery.toLowerCase()) ||
    kol.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  )

  return (
    <>
      <SEO 
        title="SCIKOL - SCI Field Key Opinion Leader Navigation"
        description="Comprehensive navigation platform for SCI (Science Citation Index) key opinion leaders, academic research, and scholarly resources in natural sciences. Discover top scientists and research institutions."
        keywords="SCI, natural sciences, academic research, scholarly journals, key opinion leaders, citation index,自然科学,学术研究,学术期刊,关键意见领袖,引用指数"
        type="website"
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo and Title */}
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-emerald-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl font-bold">SC</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">SCIKOL</h1>
                <p className="text-gray-600 text-sm">SCI Field KOL Navigation Website</p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                    activeTab === item.id
                      ? 'bg-green-50 text-green-600 font-medium'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Search Bar */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search scientists, institutions, research fields..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent w-64"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <span className="absolute left-3 top-3 text-gray-400">🔍</span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-50 to-emerald-100">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-4xl">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <span className="text-green-500">🔬</span>
              <span className="text-sm font-medium">Science Citation Index (SCI)</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Global SCI Field
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600"> Key Opinion Leader</span>
              Navigation
            </h1>
            
            <p className="text-xl text-gray-700 mb-10 max-w-3xl">
              We provide you with navigation to key opinion leader (KOL) information in the global SCI (Science Citation Index) field.
              Explore top scientists, research institutions, core journals, and cutting-edge technology fields.
            </p>

            <div className="hidden"></div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <ShopSection />

      {/* Scientific News Section */}
      <section className="py-16 bg-green-50">
        <div className="container mx-auto px-4">
          <NewsSection website="scikol" limit={10000} title="SCI Research News" themeColor="green" />
        </div>
      </section>

      {/* Featured KOLs Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Top SCI Scientists</h2>
              <p className="text-gray-600">Leading figures and Nobel laureates in various natural sciences and engineering fields</p>
            </div>
            </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredKOLs.map((kol) => (
              <div key={kol.id} className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <span className="inline-block px-3 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full mb-2">
                        {kol.category}
                      </span>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{kol.name}</h3>
                      <p className="text-gray-600 text-sm mb-1">{kol.institution}</p>
                    </div>
                    <div className="flex items-center bg-gray-100 rounded-lg px-2 py-1">
                      <span className="text-green-500 mr-1">h-index</span>
                      <span className="font-medium">{kol.hIndex}</span>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 mb-4">{kol.researchArea}</p>
                  
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Major Awards:</h4>
                    <div className="flex flex-wrap gap-2">
                      {kol.awards.map((award, index) => (
                        <span key={index} className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded">
                          {award}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {kol.tags.map((tag, index) => (
                      <span key={index} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <a 
                    href={kol.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-green-600 font-medium hover:text-green-700 transition-colors duration-200"
                  >
                    Visit Scientist Profile
                    <span className="ml-2">→</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Institutions Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Top Research Institutions</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Universities and research institutions with the highest SCI research output globally
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {topInstitutions.map((institution, index) => (
              <div key={index} className="bg-white rounded-xl p-4 border border-gray-200 hover:shadow-md transition-all duration-200">
                <div className="text-3xl text-center mb-3">
                  {institution.rank === 1 ? '🥇' : institution.rank === 2 ? '🥈' : institution.rank === 3 ? '🥉' : '🏛️'}
                </div>
                <h3 className="font-bold text-gray-900 text-center mb-2">{institution.name}</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex justify-between">
                    <span>Global Rank:</span>
                    <span className="font-medium">#{institution.rank}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Location:</span>
                    <span className="font-medium">{institution.location}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>SCI Researchers:</span>
                    <span className="font-medium">{institution.researchers}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                  <span className="text-white text-lg font-bold">SC</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">SCIKOL</h3>
                  <p className="text-gray-400 text-sm">SCI Field KOL Navigation Website</p>
                </div>
              </div>
              <p className="text-gray-400">
                We are committed to providing key opinion leader (KOL) information in the global SCI (Science Citation Index) field.
                This website collects and organizes information on top scientists, research institutions, core journals, and cutting-edge technology fields.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">Quick Links</h4>
              <ul className="space-y-3">
                <li>
                  <a 
                    href="#"
                    className="text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    Top Scientists
                  </a>
                </li>
                <li>
                  <a 
                    href="#"
                    className="text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    SCI Journals
                  </a>
                </li>
                <li>
                  <a 
                    href="#"
                    className="text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    Research Institutions
                  </a>
                </li>
                <li>
                  <a 
                    href="#"
                    className="text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    Cutting-edge Technology
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">Contact & Collaboration</h4>
              <div className="space-y-4 text-gray-400">
                <div className="flex items-center space-x-3">
                  <span>📧</span>
                  <a 
                    href="mailto:info@scikol.com"
                    className="hover:text-white transition-colors duration-200"
                  >
                    info@scikol.com
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <span>🌐</span>
                  <a 
                    href="https://scikol.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors duration-200"
                  >
                    scikol.com
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <span>🔗</span>
                  <a 
                    href="https://sscikol.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors duration-200"
                  >
                    sscikol.com
                  </a>
                </div>
                <p className="text-sm pt-4">
                  This website is dedicated to promoting exchange and collaboration in scientific research and technology.
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500">
            <p>© 2026 SCIKOL. All rights reserved. | SCI Field Key Opinion Leader Navigation Website</p>
            <p className="text-sm mt-2">This website collects and organizes information on scientists, institutions, and research achievements in the global SCI field.</p>
          </div>
        </div>
      </footer>
    </div>
    </>
  )
}

export default App